import {addLineItems} from '@/cart/cart-store';
import {endDrawer} from '@/components/drawers';
import '@/components/QuantitySelectorRegister';
import {logError} from '@/utilities/log';
import {replaceUrlParam} from '@/utilities/replace-url-param';
import {cloneElement, toChildArray, VNode} from 'preact';
import {TargetedEvent} from 'preact/compat';
import {useEffect, useState} from 'preact/hooks';

type Variants = {
  id: number;
  options: string[];
}[];

export type BasicBuyStackProps = {
  children: VNode;
  selectedVariant?: number;
  variants: Variants;
};

const getSelectedOptions = (formData: FormData) =>
  Array.from(formData)
    .filter(([key]) => /options\[(.+)\]/.test(key))
    .map(([, value]) => value) as string[];

const getVariantBySelectedOptions = (
  selectedOptions: string[],
  variants: Variants
) =>
  variants.find(({options}) =>
    selectedOptions.every((option) => options.includes(option))
  );

/**
 * A custom element that wraps a form.
 * It updates the selected variants id when the form data changes
 * and adds the selected variant to the cart when the form is submitted.
 * It also keeps the 'variant' URL param in sync to enable variant deep linking.
 *
 * @param variants - The product variants
 * @param children - The form
 * @returns A clone of the form with additional props
 */
export default function BasicBuyStack({
  children,
  variants,
  selectedVariant,
}: BasicBuyStackProps) {
  const [formData, setFormData] = useState(new FormData());
  const selectedVariantId =
    Array.from(formData.entries()).length == 0
      ? selectedVariant
      : getVariantBySelectedOptions(getSelectedOptions(formData), variants)?.id;

  useEffect(() => {
    if (!selectedVariantId) return;
    replaceUrlParam('variant', String(selectedVariantId));
  }, [selectedVariantId]);

  const handleChange = (e: Event) => {
    const target = e.currentTarget as HTMLFormElement | null;
    if (!target) return;
    setFormData(new FormData(target));
  };

  const handleSubmit = (e: TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedVariantId) return;
    addLineItems([
      {
        id: selectedVariantId,
        quantity: Number(formData.get('quantity') ?? 1),
      },
    ]).catch(logError);
    endDrawer.open();
  };

  const formNode = toChildArray(children.props.children).find(
    (child): child is VNode<{dataSelectedVariantId: string}> =>
      typeof child !== 'string' &&
      typeof child !== 'number' &&
      child.type === 'form'
  );
  return formNode
    ? cloneElement(formNode, {
        'data-selected-variant-id':
          selectedVariantId ?? formNode.props.dataSelectedVariantId,
        onChange: handleChange,
        onSubmit: handleSubmit,
      })
    : null;
}
